import { memo } from 'react';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ScheduleIcon from '@material-ui/icons/Schedule';
import StopIcon from '@material-ui/icons/Stop';
import { WarningIcon } from '../icons/WarningIcon';
import Badge from './Badge';
import './styles/statusBadge.css';

interface StatusConfig {
  text: string;
  className: string;
  icon: React.ReactNode;
}

const STATUS_CONFIG: Record<string, StatusConfig> = {
  // CREATE operation statuses
  SUBMITTED: {
    text: 'Submitted',
    className: 'pc-badge-status-submitted',
    icon: <ScheduleIcon />,
  },
  FAILED: {
    text: 'Failed',
    className: 'pc-badge-status-failed',
    icon: <WarningIcon color="var(--pc-status-failed-text, #991B1B)" />,
  },
  FAILED_LOGIN: {
    text: 'Failed',
    className: 'pc-badge-status-failed',
    icon: <WarningIcon color="var(--pc-status-failed-text, #991B1B)" />,
  },
  SUCCESSFUL: {
    text: 'Ready',
    className: 'pc-badge-status-ready',
    icon: <CheckCircleIcon />,
  },

  PROCESSING: {
    text: 'Processing',
    className: 'pc-badge-status-processing',
    icon: <ScheduleIcon />,
  },
  GENERATING: {
    text: 'Generating & Executing',
    className: 'pc-badge-status-processing',
    icon: <ScheduleIcon />,
  },
  GENERATE_SUCCESSFUL: {
    text: 'Completed',
    className: 'pc-badge-status-generate-successful',
    icon: <CheckCircleIcon />,
  },
  GENERATE_FAILED: {
    text: 'Failed',
    className: 'pc-badge-status-generate-failed',
    icon: <WarningIcon color="var(--pc-status-failed-text, #991B1B)" />,
  },
  GENERATE_STOPPING: {
    text: 'Stopping',
    className: 'pc-badge-status-processing',
    icon: <ScheduleIcon />,
  },
  GENERATE_STOPPED: {
    text: 'Stopped',
    className: 'pc-badge-status-stopped',
    icon: <StopIcon style={{ color: 'var(--ec-stop-run-accent)' }} />,
  },
  EXECUTING: {
    text: 'Re-executing',
    className: 'pc-badge-status-processing',
    icon: <ScheduleIcon />,
  },
  EXECUTE_SUCCESSFUL: {
    text: 'Completed',
    className: 'pc-badge-status-execute-successful',
    icon: <CheckCircleIcon />,
  },
  EXECUTE_FAILED: {
    text: 'Failed',
    className: 'pc-badge-status-failed',
    icon: <WarningIcon color="var(--pc-status-failed-text, #991B1B)" />,
  },
  EXECUTE_STOPPING: {
    text: 'Stopping',
    className: 'pc-badge-status-processing',
    icon: <ScheduleIcon />,
  },
  EXECUTE_STOPPED: {
    text: 'Stopped',
    className: 'pc-badge-status-stopped',
    icon: <StopIcon style={{ color: 'var(--ec-stop-run-accent)' }} />,
  },
  ENHANCING: {
    text: 'Enhancing',
    className: 'pc-badge-status-processing',
    icon: <ScheduleIcon />,
  },
  ENHANCE_SUCCESSFUL: {
    text: 'Completed',
    className: 'pc-badge-status-ready',
    icon: <CheckCircleIcon />,
  },
  ENHANCE_FAILED: {
    text: 'Failed',
    className: 'pc-badge-status-failed',
    icon: <WarningIcon color="var(--pc-status-failed-text, #991B1B)" />,
  },
  ENHANCE_STOPPING: {
    text: 'Stopping',
    className: 'pc-badge-status-processing',
    icon: <ScheduleIcon />,
  },
  ENHANCE_STOPPED: {
    text: 'Stopped',
    className: 'pc-badge-status-stopped',
    icon: <StopIcon style={{ color: 'var(--ec-stop-run-accent)' }} />,
  },
  DEFAULT: {
    text: 'Unknown',
    className: 'pc-badge-status-failed',
    icon: <WarningIcon color="var(--pc-status-failed-text, #991B1B)" />,
  },
};

interface StatusBadgeProps {
  status?: string;
  hasPreviousRuns?: boolean;
}

const StatusBadge = memo(
  ({ status, hasPreviousRuns = false }: StatusBadgeProps) => {
    let config = (status && STATUS_CONFIG[status]) || STATUS_CONFIG.DEFAULT;

    // If status is EXECUTING and this is the first run, show "Generating & Executing"
    if (status === 'EXECUTING' && !hasPreviousRuns) {
      config = {
        text: 'Generating & Executing',
        className: 'pc-badge-status-processing',
        icon: <ScheduleIcon />,
      };
    }

    return (
      <Badge
        text={config.text}
        className={config.className}
        icon={config.icon}
      />
    );
  },
);

export default StatusBadge;
